import { logEvent, setUserId, setUserProperties } from 'firebase/analytics'
import type { User } from '../../types/api.types'
import { getFirebaseAnalytics, initFirebaseAnalytics, isFirebaseAnalyticsConfigured } from './firebase'
import { getAnalyticsUserType, type AnalyticsUserType } from './userType'

export type { AnalyticsUserType }
export { getAnalyticsUserType, isFirebaseAnalyticsConfigured, initFirebaseAnalytics }

export async function initAnalytics(): Promise<void> {
  await initFirebaseAnalytics()
}

function analyticsUserProperties(user: User): Record<string, string> {
  const userType = getAnalyticsUserType(user)
  return {
    user_type: userType,
    role: user.role,
    organization_id: String(user.organization_id),
  }
}

export async function setAnalyticsUser(user: User): Promise<void> {
  const analytics = await getFirebaseAnalytics()
  if (!analytics) {
    return
  }

  setUserId(analytics, String(user.id))
  setUserProperties(analytics, analyticsUserProperties(user))
}

/** GA4 recommended login event — platform admin vs tenant logins. */
export async function trackLogin(user: User): Promise<void> {
  const analytics = await getFirebaseAnalytics()
  if (!analytics) {
    return
  }

  const userType = getAnalyticsUserType(user)
  await setAnalyticsUser(user)

  logEvent(analytics, 'login', {
    method: 'email',
    user_type: userType,
    role: user.role,
  })
}

export async function trackLoginFailed(params: {
  status?: number
  reason?: string
}): Promise<void> {
  const analytics = await getFirebaseAnalytics()
  if (!analytics) {
    return
  }

  logEvent(analytics, 'login_failed', {
    method: 'email',
    status_code: params.status ?? 0,
    reason: params.reason ?? 'unknown',
  })
}

export async function trackLogout(user: User | null): Promise<void> {
  const analytics = await getFirebaseAnalytics()
  if (!analytics) {
    return
  }

  logEvent(analytics, 'logout', {
    user_type: user ? getAnalyticsUserType(user) : 'unknown',
  })
}
